/**
 * EmailPreferences - User preferences for email notifications (Issue #1265)
 *
 * Allows users to control which types of email notifications they receive.
 * All preferences default to enabled (opt-out model) for better user experience.
 */

import React, { useState, useEffect } from 'react';
import { Mail, Save, X, AlertCircle } from 'lucide-react';
import { notificationApi } from '../services/api';
import { NotificationPreferences, NotificationPreferencesUpdate } from '../types';
import { extractApiError } from '../utils/errorUtils';

interface EmailPreferencesProps {
  /** Show as compact inline toggles (default: false) */
  compact?: boolean;
}

/**
 * Settings component for controlling email notification preferences
 */
export default function EmailPreferences({ compact = false }: EmailPreferencesProps) {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [localPreferences, setLocalPreferences] = useState<NotificationPreferencesUpdate>({});
  const [successTimeoutId, setSuccessTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // Load preferences on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutId) {
        clearTimeout(successTimeoutId);
      }
    };
  }, [successTimeoutId]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationApi.getPreferences();
      setPreferences(data);
      // Initialize local preferences with current values
      setLocalPreferences({
        email_preorder_confirmation_enabled: data.email_preorder_confirmation_enabled,
        email_preorder_cancellation_enabled: data.email_preorder_cancellation_enabled,
        email_preorder_status_update_enabled: data.email_preorder_status_update_enabled,
        email_inquiry_status_update_enabled: data.email_inquiry_status_update_enabled,
        email_feedback_confirmation_enabled: data.email_feedback_confirmation_enabled,
        email_new_review_enabled: data.email_new_review_enabled,
        email_pending_fair_rejection_enabled: data.email_pending_fair_rejection_enabled,
        email_slot_available_enabled: data.email_slot_available_enabled,
      });
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load email preferences:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (field: keyof NotificationPreferencesUpdate) => {
    setLocalPreferences(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
    setHasChanges(true);
    setSuccessMessage(null); // Clear success message when making changes
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Clear any existing timeout
      if (successTimeoutId) {
        clearTimeout(successTimeoutId);
        setSuccessTimeoutId(null);
      }

      const updated = await notificationApi.updatePreferences(localPreferences);
      setPreferences(updated);
      setHasChanges(false);
      setSuccessMessage('Email preferences saved successfully!');

      // Auto-dismiss success message after 3 seconds
      const timeoutId = setTimeout(() => {
        setSuccessMessage(null);
        setSuccessTimeoutId(null);
      }, 3000);
      setSuccessTimeoutId(timeoutId);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to update email preferences:', apiError);
      setError(apiError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (preferences) {
      setLocalPreferences({
        email_preorder_confirmation_enabled: preferences.email_preorder_confirmation_enabled,
        email_preorder_cancellation_enabled: preferences.email_preorder_cancellation_enabled,
        email_preorder_status_update_enabled: preferences.email_preorder_status_update_enabled,
        email_inquiry_status_update_enabled: preferences.email_inquiry_status_update_enabled,
        email_feedback_confirmation_enabled: preferences.email_feedback_confirmation_enabled,
        email_new_review_enabled: preferences.email_new_review_enabled,
        email_pending_fair_rejection_enabled: preferences.email_pending_fair_rejection_enabled,
        email_slot_available_enabled: preferences.email_slot_available_enabled,
      });
    }
    setHasChanges(false);
    setError(null);
    setSuccessMessage(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-gray-600">Loading email preferences...</div>
      </div>
    );
  }

  if (compact) {
    // Compact view - just show toggle count or icon
    return (
      <div className="flex items-center gap-2">
        <Mail className="w-4 h-4 text-secondary" />
        <span className="text-sm text-gray-700">Email Preferences</span>
      </div>
    );
  }

  const emailPreferenceFields = [
    {
      key: 'email_slot_available_enabled' as keyof NotificationPreferencesUpdate,
      label: 'Slot Available Notifications',
      description: 'Receive emails when a waitlisted fair slot becomes available',
      category: 'Waitlist & Slots',
    },
    {
      key: 'email_preorder_confirmation_enabled' as keyof NotificationPreferencesUpdate,
      label: 'Pre-Order Confirmations',
      description: 'Receive confirmation emails when customers place pre-orders',
      category: 'Orders & Transactions',
    },
    {
      key: 'email_preorder_cancellation_enabled' as keyof NotificationPreferencesUpdate,
      label: 'Pre-Order Cancellations',
      description: 'Receive emails when customers cancel pre-orders',
      category: 'Orders & Transactions',
    },
    {
      key: 'email_preorder_status_update_enabled' as keyof NotificationPreferencesUpdate,
      label: 'Pre-Order Status Updates',
      description: 'Receive emails when pre-order status changes',
      category: 'Orders & Transactions',
    },
    {
      key: 'email_inquiry_status_update_enabled' as keyof NotificationPreferencesUpdate,
      label: 'Inquiry Status Updates',
      description: 'Receive emails when your inquiries are updated',
      category: 'Communication',
    },
    {
      key: 'email_feedback_confirmation_enabled' as keyof NotificationPreferencesUpdate,
      label: 'Feedback Confirmations',
      description: 'Receive confirmation emails when you submit feedback',
      category: 'Communication',
    },
    {
      key: 'email_new_review_enabled' as keyof NotificationPreferencesUpdate,
      label: 'New Review Notifications',
      description: 'Receive emails when customers leave reviews for your business',
      category: 'Reviews & Ratings',
    },
    {
      key: 'email_pending_fair_rejection_enabled' as keyof NotificationPreferencesUpdate,
      label: 'Fair Rejection Notifications',
      description: 'Receive emails when your fair submissions are rejected',
      category: 'Fair Management',
    },
  ];

  // Group by category
  const categories = {
    'Waitlist & Slots': emailPreferenceFields.filter(f => f.category === 'Waitlist & Slots'),
    'Orders & Transactions': emailPreferenceFields.filter(f => f.category === 'Orders & Transactions'),
    'Communication': emailPreferenceFields.filter(f => f.category === 'Communication'),
    'Reviews & Ratings': emailPreferenceFields.filter(f => f.category === 'Reviews & Ratings'),
    'Fair Management': emailPreferenceFields.filter(f => f.category === 'Fair Management'),
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Email Notification Preferences</h3>
          <p className="text-sm text-gray-500 mt-1">
            Control which email notifications you receive. You can opt out of any notification type.
          </p>
        </div>
        <Mail className="w-6 h-6 text-secondary" />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(categories).map(([categoryName, fields]) => (
          <div key={categoryName}>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">{categoryName}</h4>
            <div className="space-y-3">
              {fields.map((field) => (
                <div key={field.key} className="flex items-start justify-between py-3 border-b border-gray-100 last:border-0">
                  <div className="flex-1 pr-4">
                    <p className="text-sm font-medium text-gray-900">{field.label}</p>
                    <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                  </div>
                  <button
                    onClick={() => handleToggle(field.key)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-dusty-teal focus:ring-offset-2 ${
                      localPreferences[field.key] ? 'bg-primary' : 'bg-gray-200'
                    }`}
                    role="switch"
                    aria-checked={localPreferences[field.key]}
                    aria-label={`Toggle ${field.label}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        localPreferences[field.key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Save/Cancel buttons - only show when there are changes */}
      {hasChanges && (
        <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-teal-400 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
          <button
            onClick={handleCancel}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
          >
            <X className="w-4 h-4" />
            <span>Cancel</span>
          </button>
        </div>
      )}

      <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
        <p>
          <strong>Note:</strong> All email notifications are enabled by default. You can opt out of any type at any time.
          Changes take effect immediately.
        </p>
      </div>
    </div>
  );
}
